import { makeAutoObservable, computed, runInAction } from 'mobx';
import type { DealType } from './types';
import type { RootStore } from './RootStore';

export class DealTypeStore {
  dealTypes = new Map<string, DealType>();
  private root: RootStore;

  constructor(root: RootStore) {
    this.root = root;
    makeAutoObservable(this, { all: computed });
  }

  get all(): DealType[] {
    const result: DealType[] = [];
    for (const dt of this.dealTypes.values()) {
      if (!dt.isDeleted) result.push(dt);
    }
    return result.sort((a, b) => (a.name < b.name ? -1 : 1));
  }

  getById(id: string): DealType | undefined {
    const dt = this.dealTypes.get(id);
    return dt && !dt.isDeleted ? dt : undefined;
  }

  loadFromRecords(records: Record<string, unknown>[]): void {
    runInAction(() => {
      for (const record of records) {
        this.dealTypes.set(record.id as string, record as unknown as DealType);
      }
    });
  }

  applyChanges(changes: { id: string; data?: Record<string, unknown>; isDeleted: boolean }[]): void {
    runInAction(() => {
      for (const change of changes) {
        if (change.isDeleted) this.dealTypes.delete(change.id);
        else if (change.data) this.dealTypes.set(change.id, change.data as unknown as DealType);
      }
    });
  }

  removeFromMap(id: string): void {
    this.dealTypes.delete(id);
  }

  create(data: Partial<DealType>): DealType {
    const dt: DealType = {
      id: crypto.randomUUID(),
      name: data.name ?? '',
      description: data.description,
      customFieldsSchema: data.customFieldsSchema,
      orgId: '',
      syncVersion: 0,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.dealTypes.set(dt.id, dt);
    this.root.syncStore.queueMutation('dealType', 'create', dt.id, dt);
    return dt;
  }

  update(id: string, patch: Partial<DealType>): void {
    const existing = this.dealTypes.get(id);
    if (!existing) return;
    this.dealTypes.set(id, { ...existing, ...patch, updatedAt: new Date().toISOString() });
    this.root.syncStore.queueMutation('dealType', 'update', id, patch);
  }

  delete(id: string): void {
    const existing = this.dealTypes.get(id);
    if (existing) {
      this.dealTypes.set(id, { ...existing, isDeleted: true, updatedAt: new Date().toISOString() });
    }
    this.root.syncStore.queueMutation('dealType', 'delete', id);
  }

  clear(): void { this.dealTypes.clear(); }
}
